import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  Snackbar,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Timer as TimerIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { employeesApi, authApi } from '../../services/api';

function EmployeesTab() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [sessionMinutes, setSessionMinutes] = useState(60);
  const [sessionMinutesInput, setSessionMinutesInput] = useState('60');
  const [savingSession, setSavingSession] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    gitlab_username: '',
    skills: {},
    is_active: true,
    max_concurrent_projects: 3,
    new_password: '',
  });

  const [newSkill, setNewSkill] = useState({ name: '', level: 'common' });

  const commonSkills = [
    'JavaScript', 'Python', 'React', 'Node.js', 'Flask', 'Django', 
    'HTML', 'CSS', 'SQL', 'MongoDB', 'PostgreSQL', 'AWS', 'Docker',
    'Git', 'TypeScript', 'Vue.js', 'Angular', 'Express.js', 'FastAPI',
    'Java', 'C#', 'PHP', 'Ruby', 'Go', 'Rust', 'Kubernetes', 'DevOps'
  ];

  useEffect(() => {
    fetchEmployees();
    fetchSessionSettings();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await employeesApi.getAll();
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSessionSettings = async () => {
    try {
      const res = await authApi.getSessionSettings();
      setSessionMinutes(res.data.session_length_minutes);
      setSessionMinutesInput(String(res.data.session_length_minutes));
    } catch (error) {
      console.error('Error fetching session settings:', error);
    }
  };

  const handleSaveSessionSettings = async () => {
    const minutes = parseInt(sessionMinutesInput, 10);
    if (!minutes || minutes < 5) {
      setSnackbar({ open: true, message: 'Session length must be at least 5 minutes', severity: 'error' });
      return;
    }
    setSavingSession(true);
    try {
      await authApi.updateSessionSettings({ session_length_minutes: minutes });
      setSessionMinutes(minutes);
      setSnackbar({ open: true, message: 'Session settings saved', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to save session settings', severity: 'error' });
    } finally {
      setSavingSession(false);
    }
  };

  const handleAddEmployee = () => {
    setSelectedEmployee(null);
    setFormData({
      name: '',
      email: '',
      gitlab_username: '',
      skills: {},
      is_active: true,
      max_concurrent_projects: 3,
      new_password: '',
    });
    setOpenDialog(true);
  };

  const handleEditEmployee = (employee) => {
    setSelectedEmployee(employee);
    setFormData({
      name: employee.name,
      email: employee.email || '',
      gitlab_username: employee.gitlab_username || '',
      skills: employee.skills || {},
      is_active: employee.is_active,
      max_concurrent_projects: employee.max_concurrent_projects || 3,
      new_password: '',
    });
    setOpenDialog(true);
  };

  const handleDeleteEmployee = async (employeeId) => {
    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        await employeesApi.delete(employeeId);
        setEmployees(employees.filter(e => e.id !== employeeId));
        setSnackbar({ open: true, message: 'Employee deleted successfully', severity: 'success' });
      } catch (error) {
        setSnackbar({ open: true, message: 'Failed to delete employee', severity: 'error' });
      }
    }
  };

  const handleSaveEmployee = async () => {
    try {
      if (!formData.name.trim()) {
        setSnackbar({ open: true, message: 'Employee name is required', severity: 'error' });
        return;
      }
      if (formData.new_password && formData.new_password.length < 8) {
        setSnackbar({ open: true, message: 'Password must be at least 8 characters', severity: 'error' });
        return;
      }

      const payload = { ...formData };
      if (payload.new_password) {
        payload.password = payload.new_password;
      }
      delete payload.new_password;

      if (selectedEmployee) {
        const response = await employeesApi.update(selectedEmployee.id, payload);
        setEmployees(employees.map(e => e.id === selectedEmployee.id ? response.data : e));
        setSnackbar({ open: true, message: 'Employee updated successfully', severity: 'success' });
      } else {
        const response = await employeesApi.create(payload);
        setEmployees([...employees, response.data]);
        setSnackbar({ open: true, message: 'Employee created successfully', severity: 'success' });
      }
      setOpenDialog(false);
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to save employee', severity: 'error' });
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddSkill = () => {
    if (!newSkill.name.trim()) return;
    
    setFormData(prev => ({
      ...prev,
      skills: {
        ...prev.skills,
        [newSkill.name.toLowerCase()]: newSkill.level
      }
    }));
    setNewSkill({ name: '', level: 'common' });
  };

  const handleRemoveSkill = (skillName) => {
    setFormData(prev => {
      const newSkills = { ...prev.skills };
      delete newSkills[skillName];
      return { ...prev, skills: newSkills };
    });
  };

  const handleSkillLevelChange = (skillName, level) => {
    setFormData(prev => ({
      ...prev,
      skills: {
        ...prev.skills,
        [skillName]: level
      }
    }));
  };

  const getSkillColor = (level) => {
    if (level === 'expert') return 'success';
    if (level === 'challenging') return 'warning';
    return 'default';
  };

  return (
    <Box>
      {/* Session Settings */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={1.5}>
          <TimerIcon fontSize="small" color="action" />
          <Typography variant="subtitle1" fontWeight={600}>Session Settings</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <TextField
            label="Session timeout"
            type="number"
            value={sessionMinutesInput}
            onChange={(e) => setSessionMinutesInput(e.target.value)}
            inputProps={{ min: 5, max: 480 }}
            size="small"
            sx={{ width: 180 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">min</InputAdornment>,
            }}
            helperText="Auto-logout after this many minutes of inactivity"
          />
          <Button
            variant="contained"
            onClick={handleSaveSessionSettings}
            disabled={savingSession || parseInt(sessionMinutesInput, 10) === sessionMinutes}
            size="small"
          >
            Save
          </Button>
          {sessionMinutes !== parseInt(sessionMinutesInput, 10) && parseInt(sessionMinutesInput, 10) >= 5 && (
            <Typography variant="caption" color="text.secondary">
              Current: {sessionMinutes} min
            </Typography>
          )}
        </Box>
      </Paper>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">
          Team Members
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddEmployee}
        >
          Add Employee
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>GitLab Username</TableCell>
              <TableCell>Skills</TableCell>
              <TableCell>Max Projects</TableCell>
              <TableCell>Password</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employees.map((employee) => (
              <TableRow key={employee.id}>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <PersonIcon sx={{ mr: 1 }} />
                    {employee.name}
                  </Box>
                </TableCell>
                <TableCell>{employee.email || 'N/A'}</TableCell>
                <TableCell>
                  {employee.gitlab_username
                    ? <Chip label={employee.gitlab_username} size="small" variant="outlined" />
                    : <Typography variant="body2" color="text.secondary">—</Typography>}
                </TableCell>
                <TableCell>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {Object.entries(employee.skills || {}).slice(0, 3).map(([skill, level]) => (
                      <Chip
                        key={skill}
                        label={`${skill} · ${level}`}
                        size="small"
                        color={getSkillColor(level)}
                        variant="outlined"
                      />
                    ))}
                    {Object.keys(employee.skills || {}).length > 3 && (
                      <Chip
                        label={`+${Object.keys(employee.skills).length - 3} more`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>{employee.max_concurrent_projects || 3}</TableCell>
                <TableCell>
                  {employee.has_password
                    ? <Chip icon={<LockIcon />} label="Set" size="small" color="success" variant="outlined" />
                    : <Chip label="Not set" size="small" color="warning" variant="outlined" />}
                </TableCell>
                <TableCell>
                  <Chip
                    label={employee.is_active ? 'Active' : 'Inactive'}
                    color={employee.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEditEmployee(employee)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteEmployee(employee.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedEmployee ? 'Edit Employee' : 'Add New Employee'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Full Name"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="GitLab Username"
                  value={formData.gitlab_username}
                  onChange={(e) => handleFormChange('gitlab_username', e.target.value)}
                  fullWidth
                  helperText="Enter with or without @. Used to auto-assign imported GitLab issues."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={selectedEmployee ? 'New Password (leave blank to keep current)' : 'Password (auto-generated if blank)'}
                  type="password"
                  value={formData.new_password}
                  onChange={(e) => handleFormChange('new_password', e.target.value)}
                  fullWidth
                  autoComplete="new-password"
                  helperText="Minimum 8 characters"
                  InputProps={{ startAdornment: <InputAdornment position="start"><LockIcon fontSize="small" /></InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Max Concurrent Projects"
                  type="number"
                  value={formData.max_concurrent_projects}
                  onChange={(e) => handleFormChange('max_concurrent_projects', parseInt(e.target.value))}
                  inputProps={{ min: 1, max: 10 }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) => handleFormChange('is_active', e.target.checked)}
                    />
                  }
                  label="Active Employee"
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Skills & Expertise
              </Typography>
              
              {/* Add New Skill */}
              <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Add New Skill
                </Typography>
                <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                  <TextField
                    label="Skill Name"
                    value={newSkill.name}
                    onChange={(e) => setNewSkill(prev => ({ ...prev, name: e.target.value }))}
                    size="small"
                    sx={{ minWidth: 200 }}
                  />
                  <ToggleButtonGroup
                    value={newSkill.level}
                    exclusive
                    onChange={(_, v) => v && setNewSkill(prev => ({ ...prev, level: v }))}
                    size="small"
                  >
                    <ToggleButton value="common">Common</ToggleButton>
                    <ToggleButton value="challenging">Challenging</ToggleButton>
                    <ToggleButton value="expert">Expert</ToggleButton>
                  </ToggleButtonGroup>
                  <Button
                    variant="contained"
                    onClick={handleAddSkill}
                    size="small"
                    disabled={!newSkill.name.trim()}
                  >
                    Add
                  </Button>
                </Box>
              </Box>

              {/* Existing Skills */}
              {Object.entries(formData.skills).map(([skill, level]) => (
                <Accordion key={skill}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Typography>{skill}</Typography>
                      <Chip
                        label={level}
                        size="small"
                        color={getSkillColor(level)}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                      <ToggleButtonGroup
                        value={level}
                        exclusive
                        onChange={(_, v) => v && handleSkillLevelChange(skill, v)}
                        size="small"
                      >
                        <ToggleButton value="common">Common</ToggleButton>
                        <ToggleButton value="challenging">Challenging</ToggleButton>
                        <ToggleButton value="expert">Expert</ToggleButton>
                      </ToggleButtonGroup>
                      <Button
                        color="error"
                        onClick={() => handleRemoveSkill(skill)}
                        size="small"
                      >
                        Remove
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}

              {Object.keys(formData.skills).length === 0 && (
                <Typography color="text.secondary" sx={{ mt: 2 }}>
                  No skills added yet. Add skills to help with project assignment.
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveEmployee} variant="contained">
            {selectedEmployee ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default EmployeesTab;